/**
 * Peak's DEMO catalog — the IMLIL range, and what a grid falls back to when nothing real resolves.
 *
 * It lives in ui-react, not in the storefront app, because the BUILDER needs it too: a seller opening
 * Peak on an empty store would otherwise get the house fallback (a clothing shop, "Cotton Crew Tee")
 * inside a supplement theme (spec 0032 §3 always listed `catalog` as one of a theme's four pieces).
 *
 * TYPE-ONLY IMPORTS on purpose: `sections/_shared/product.ts` reads this module through
 * `themes/demoCatalogs.ts`, so anything but a type import here would close an import cycle.
 *
 * Prices carry the STORE's currency rather than one of their own — the same demo has to read correctly
 * in a MAD shop and in a EUR one (0049).
 */
const IMG = '/demo/supplements';
function product(currency, p) {
    return {
        id: p.id,
        name: p.name,
        slug: p.slug,
        description: p.desc,
        price: { amount: p.price, currency },
        compareAtPrice: p.compareAt ? { amount: p.compareAt, currency } : null,
        images: [{ url: `${IMG}/${p.image}`, alt: p.alt, isCover: true }],
        inventory: { tracked: false, quantity: null },
        seo: { metaTitle: null, metaDescription: null },
        status: 'active',
        tags: p.badge ? [p.badge] : [],
        vendor: 'IMLIL',
        rating: p.rating,
        // A supplement's real variant is the LENGTH OF THE CURE, not a size. Naming the option "Cure"
        // is what makes the buy-box read like a supplement shop instead of a clothing one.
        options: [
            { name: 'Cure', values: (p.cures ?? ['1 mois', '3 mois']).map((label) => ({ label })) },
        ],
    };
}
/** The IMLIL range, in merchandising order. */
export function peakDemoProducts(currency) {
    return [
        product(currency, {
            id: 'p-imlil-daily',
            name: 'Daily — Multivitamine complète',
            slug: 'daily-multivitamine-complete',
            price: 29900,
            compareAt: 34900,
            badge: 'custom-badge-1',
            image: 'product-daily.webp',
            alt: 'Flacon Daily, multivitamine complète IMLIL',
            rating: { value: 4.8, count: 412 },
            desc: '22 nutriments dosés à l’apport de référence, en deux gélules au petit-déjeuner.',
        }),
        product(currency, {
            id: 'p-imlil-nuit',
            name: 'Nuit — Magnésium & Glycine',
            slug: 'nuit-magnesium-glycine',
            price: 18900,
            image: 'product-nuit.webp',
            alt: 'Flacon Nuit, magnésium bisglycinate IMLIL',
            rating: { value: 4.9, count: 338 },
            desc: 'Bisglycinate, la forme que l’intestin tolère. À prendre une heure avant le coucher.',
        }),
        product(currency, {
            id: 'p-imlil-defense',
            name: 'Défense — Vitamine D3 + K2',
            slug: 'defense-vitamine-d3-k2',
            price: 16900,
            image: 'product-defense.webp',
            alt: 'Flacon Défense, vitamine D3 et K2 IMLIL',
            rating: { value: 4.8, count: 276 },
            desc: '2000 UI de D3 et 75 µg de K2 MK-7, pour l’hiver et les bureaux sans fenêtre.',
        }),
        product(currency, {
            id: 'p-imlil-souffle',
            name: 'Souffle — Fer & Vitamine B12',
            slug: 'souffle-fer-b12',
            price: 15900,
            image: 'product-souffle.webp',
            alt: 'Flacon Souffle, fer bisglycinate et B12 IMLIL',
            rating: { value: 4.7, count: 154 },
            desc: 'Fer bisglycinate, sans le désagrément digestif du sulfate. B12 sous forme active.',
        }),
        product(currency, {
            id: 'p-imlil-flore',
            name: 'Flore — Probiotiques 30 milliards',
            slug: 'flore-probiotiques-30-milliards',
            price: 24900,
            compareAt: 29900,
            badge: 'custom-badge-2',
            image: 'product-flore.webp',
            alt: 'Flacon Flore, probiotiques IMLIL',
            rating: { value: 4.8, count: 198 },
            desc: 'Huit souches, gélule gastro-résistante, 30 milliards d’UFC jusqu’à la date de péremption.',
            cures: ['1 mois', '2 mois'],
        }),
        product(currency, {
            id: 'p-imlil-omega',
            name: 'Oméga-3 — EPA & DHA',
            slug: 'omega-3-epa-dha',
            price: 21900,
            image: 'product-omega.webp',
            alt: 'Flacon Oméga-3 IMLIL',
            rating: { value: 4.7, count: 221 },
            desc: 'Petits poissons de pêche sauvage, distillation moléculaire, sans arrière-goût.',
        }),
        product(currency, {
            id: 'p-imlil-eclat',
            name: 'Éclat — Collagène marin',
            slug: 'eclat-collagene-marin',
            price: 32900,
            image: 'product-eclat.webp',
            alt: 'Pot Éclat, collagène marin en poudre IMLIL',
            rating: { value: 4.6, count: 143 },
            desc: 'Peptides de collagène marin et vitamine C, sans goût, à mélanger au café du matin.',
            cures: ['300 g', '600 g'],
        }),
        product(currency, {
            id: 'p-imlil-focus',
            name: 'Focus — Ashwagandha & L-théanine',
            slug: 'focus-ashwagandha-theanine',
            price: 19900,
            image: 'product-focus.webp',
            alt: 'Flacon Focus, ashwagandha et L-théanine IMLIL',
            rating: { value: 4.8, count: 187 },
            desc: 'Extrait titré à 5 % de withanolides. Pour les journées longues, pas pour dormir.',
        }),
        product(currency, {
            id: 'p-imlil-bouclier',
            name: 'Bouclier — Vitamine C & Zinc',
            slug: 'bouclier-vitamine-c-zinc',
            price: 13900,
            image: 'product-vitc.webp',
            alt: 'Flacon Bouclier, vitamine C et zinc IMLIL',
            rating: { value: 4.7, count: 262 },
            desc: 'Ascorbate de calcium, non acide pour l’estomac. Zinc bisglycinate à 10 mg.',
        }),
        product(currency, {
            id: 'p-imlil-repos',
            name: 'Repos — Mélatonine & Passiflore',
            slug: 'repos-melatonine-passiflore',
            price: 14900,
            image: 'product-repos.webp',
            alt: 'Flacon Repos, mélatonine et passiflore IMLIL',
            rating: { value: 4.6, count: 129 },
            desc: '1 mg de mélatonine — la dose qui agit, pas celle qui assomme. À prendre 30 min avant.',
        }),
        product(currency, {
            id: 'p-imlil-cheveux',
            name: 'Cheveux — Biotine & Kératine',
            slug: 'cheveux-biotine-keratine',
            price: 22900,
            compareAt: 25900,
            image: 'product-cheveux.webp',
            alt: 'Flacon Cheveux, biotine et kératine IMLIL',
            rating: { value: 4.5, count: 176 },
            desc: 'Trois mois minimum : un cheveu pousse d’un centimètre par mois, pas plus vite.',
            cures: ['3 mois', '6 mois'],
        }),
        product(currency, {
            id: 'p-imlil-souplesse',
            name: 'Souplesse — Curcuma & Poivre noir',
            slug: 'souplesse-curcuma-poivre-noir',
            price: 17900,
            image: 'product-curcuma.webp',
            alt: 'Flacon Souplesse, curcuma et poivre noir IMLIL',
            rating: { value: 4.7, count: 208 },
            desc: 'Curcumine titrée à 95 %, avec la pipérine qui la rend assimilable. Sans elle, ça ne passe pas.',
        }),
    ];
}
/**
 * Peak merchandises by OBJECTIF, not by nutrient family — c'est le rayon des compléments : personne
 * n'entre en cherchant « bisglycinate », on entre en cherchant à mieux dormir.
 *
 * QUATRE collections, pas cinq : `collection-list` plafonne à 4 colonnes, et une cinquième tuile
 * laisserait une rangée bancale (le piège n°3 du mode d'emploi, la colonne fantôme).
 */
export function peakDemoCollections() {
    const make = (id, name, slug, cover, alt, productCount, description) => ({
        id,
        name,
        slug,
        description,
        coverImage: { url: `${IMG}/${cover}`, alt },
        productCount,
    });
    return [
        make('c-energie', 'Énergie', 'energie', 'goal-energie.webp', 'Flacon IMLIL et verre d’eau sur un rebord de fenêtre au soleil du matin', 4, 'Les journées qui commencent lourdes et les fins d’après-midi qui s’effondrent.'),
        make('c-sommeil', 'Sommeil', 'sommeil', 'goal-sommeil.webp', 'Flacon IMLIL sur une table de chevet, lumière du soir', 4, 'S’endormir plus vite, se réveiller moins souvent. Sans accoutumance.'),
        make('c-immunite', 'Immunité', 'immunite', 'goal-immunite.webp', 'Flacon IMLIL sur marbre blanc avec orange et gingembre', 4, 'L’hiver, les enfants à l’école, et tout ce qui circule au bureau.'),
        make('c-beaute', 'Beauté', 'beaute', 'goal-beaute.webp', 'Pot de collagène IMLIL ouvert et dosette de poudre', 4, 'Peau, cheveux et ongles — ce qui se voit dehors se joue dedans.'),
    ];
}
//# sourceMappingURL=catalog.js.map